import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Mail, MessageSquare, Calendar, ExternalLink, ThumbsUp } from 'lucide-react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

export const Team = () => {
  const { activeProject } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [activeProject]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = activeProject?.id ? { project_id: activeProject.id } : {};
      const response = await api.get('/users', { params });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserActivity = async (userEmail) => {
    try {
      setLoadingActivity(true);
      const params = activeProject?.id ? { project_id: activeProject.id } : {};
      const response = await api.get(`/users/${encodeURIComponent(userEmail)}/activity`, { params });
      setUserActivity(response.data);
    } catch (error) {
      console.error('Failed to fetch user activity:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    fetchUserActivity(user.email);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
    setUserActivity(null);
  };

  const handleFeedbackClick = (feedbackId) => {
    navigate(`/dashboard/feedback`);
    handleCloseModal();
  };

  // Feedback type translations
  const getFeedbackTypeLabel = (type) => {
    const typeLabels = {
      tr: {
        'feature': 'Feature Request',
        'bug': 'Bug Report',
        'improvement': 'Improvement',
        'other': 'Other'
      },
      en: {
        'feature': 'Feature Request',
        'bug': 'Bug Report',
        'improvement': 'Improvement',
        'other': 'Other'
      },
      de: {
        'feature': 'Feature-Anfrage',
        'bug': 'Fehlerbericht',
        'improvement': 'Verbesserung',
        'other': 'Sonstiges'
      },
      es: {
        'feature': 'Solicitud de Función',
        'bug': 'Reporte de Error',
        'improvement': 'Mejora',
        'other': 'Otro'
      },
      fr: {
        'feature': 'Demande de Fonctionnalité',
        'bug': 'Rapport de Bug',
        'improvement': 'Amélioration',
        'other': 'Autre'
      }
    };
    const labels = typeLabels[language] || typeLabels.tr;
    return labels[type] || type;
  };

  // Feedback status translations
  const getFeedbackStatusLabel = (status) => {
    const statusLabels = {
      tr: {
        'new': 'Yeni',
        'under_review': 'İnceleniyor',
        'planned': 'Planlandı',
        'in_progress': 'Devam Ediyor',
        'completed': 'Tamamlandı',
        'rejected': 'Reddedildi',
        'duplicate': 'Tekrar',
        'backlog': 'Backlog',
        'testing': 'Test Ediliyor',
        'in_development': 'Geliştiriliyor'
      },
      en: {
        'new': 'New',
        'under_review': 'Under Review',
        'planned': 'Planned',
        'in_progress': 'In Progress',
        'completed': 'Completed',
        'rejected': 'Rejected',
        'duplicate': 'Duplicate',
        'backlog': 'Backlog',
        'testing': 'Testing',
        'in_development': 'In Development'
      },
      de: {
        'new': 'Neu',
        'under_review': 'In Prüfung',
        'planned': 'Geplant',
        'in_progress': 'In Bearbeitung',
        'completed': 'Abgeschlossen',
        'rejected': 'Abgelehnt',
        'duplicate': 'Duplikat',
        'backlog': 'Backlog',
        'testing': 'Wird getestet',
        'in_development': 'In Entwicklung'
      },
      es: {
        'new': 'Nuevo',
        'under_review': 'En Revisión',
        'planned': 'Planificado',
        'in_progress': 'En Progreso',
        'completed': 'Completado',
        'rejected': 'Rechazado',
        'duplicate': 'Duplicado',
        'backlog': 'Backlog',
        'testing': 'En Pruebas',
        'in_development': 'En Desarrollo'
      },
      fr: {
        'new': 'Nouveau',
        'under_review': 'En Révision',
        'planned': 'Planifié',
        'in_progress': 'En Cours',
        'completed': 'Terminé',
        'rejected': 'Rejeté',
        'duplicate': 'Dupliqué',
        'backlog': 'Backlog',
        'testing': 'En Test',
        'in_development': 'En Développement'
      }
    };
    const labels = statusLabels[language] || statusLabels.tr;
    return labels[status] || status;
  };

  if (loading) {
    return (
      <>
        <div className="p-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">{t.loading}</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Team Management - FeedPanels</title>
        <meta name="description" content="Invite and manage team members in FeedPanels. View who has access to your project and their activity." />
        <link rel="canonical" href="https://feedpanels.com/dashboard/team" />
      </Helmet>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2" data-testid="team-page-title">
            {t.usersTitle}
          </h1>
          <p className="text-gray-600">
            {activeProject 
              ? `${activeProject.name} ${t.usersDescription}` 
              : t.usersDescriptionNoProject}
          </p>
        </div>

        {users.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{t.noUsersYet}</h3>
            <p className="text-gray-500 dark:text-gray-400">{t.noUsersDescription}</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {users.map((user, index) => (
              <Card
                key={user.email || index}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleUserClick(user)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="w-12 h-12 bg-cyan-100 dark:bg-cyan-900/30 rounded-full flex items-center justify-center">
                        <Users className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {user.name || t.anonymous}
                          </h3>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {user.email && (
                            <div className="flex items-center space-x-1">
                              <Mail className="w-4 h-4" />
                              <span>{user.email}</span>
                            </div>
                          )}
                          {user.last_activity && (
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>
                                {format(new Date(user.last_activity), 'dd MMM yyyy')}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-4">
                          {user.feedbacks_count > 0 && (
                            <Badge variant="secondary" className="flex items-center space-x-1">
                              <MessageSquare className="w-3 h-3" />
                              <span>{user.feedbacks_count} {t.feedbacks}</span>
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* User Activity Modal */}
        <Dialog open={!!selectedUser} onOpenChange={handleCloseModal}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-3 dark:text-white">
                <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div>
                  <div className="text-lg font-semibold dark:text-white">
                    {userActivity?.user?.name || selectedUser?.name || t.anonymous}
                  </div>
                  {userActivity?.user?.email && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">{userActivity.user.email}</div>
                  )}
                </div>
              </DialogTitle>
              <DialogDescription className="dark:text-gray-400">
                {t.userActivityHistory}
              </DialogDescription>
            </DialogHeader>

            {loadingActivity ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-500 dark:text-gray-400">{t.loading}</div>
              </div>
            ) : userActivity ? (
              <Tabs defaultValue="submitted" className="mt-4">
                <TabsList className="grid w-full grid-cols-1">
                  <TabsTrigger value="submitted">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {t.submittedFeedbacks} ({userActivity.submitted_feedbacks?.length || 0})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="submitted" className="mt-4">
                  {userActivity.submitted_feedbacks?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      {t.noFeedbacksYet}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {userActivity.submitted_feedbacks?.map((feedback) => (
                        <Card
                          key={feedback.id}
                          className="cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => handleFeedbackClick(feedback.id)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                  {feedback.title}
                                </h4>
                                {feedback.description && (
                                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 line-clamp-2">
                                    {feedback.description}
                                  </p>
                                )}
                                <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                                  <span>{format(new Date(feedback.created_at), 'dd MMM yyyy')}</span>
                                  {feedback.type && (
                                    <Badge variant="outline">{getFeedbackTypeLabel(feedback.type)}</Badge>
                                  )}
                                  {feedback.status && (
                                    <Badge variant="secondary">{getFeedbackStatusLabel(feedback.status)}</Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 ml-4">
                                {feedback.upvotes > 0 && (
                                  <div className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400">
                                    <ThumbsUp className="w-4 h-4" />
                                    <span>{feedback.upvotes}</span>
                                  </div>
                                )}
                                <ExternalLink className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
